import * as React from 'react'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ChecklistIcon from '@mui/icons-material/Checklist'
import LogoutIcon from '@mui/icons-material/Logout'
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useAuth } from '@modules/auth/AuthProvider'
import * as api from '@services/api'
import { PortalBrand } from '@shared/Brand'
import { canOpenAdmin, type Surface } from './access'
import { normalizeNextPath, targetForPath } from './nextPath'

function SurfaceIcon({ id }: { id: Surface['id'] }) {
  if (id === 'admin') return <AdminPanelSettingsIcon />
  return <ChecklistIcon />
}

function SurfaceButton({
  surface,
  onOpen,
}: {
  surface: Surface
  onOpen?: () => void
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minHeight: 128,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <SurfaceIcon id={surface.id} />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            {surface.title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {surface.description}
        </Typography>
      </Stack>
      <Button
        href={onOpen ? undefined : surface.href}
        onClick={onOpen}
        variant="contained"
        sx={{ mt: 2, alignSelf: 'flex-start' }}
      >
        Åpne {surface.title.toLowerCase()}
      </Button>
    </Paper>
  )
}

export default function HomePage() {
  const { user, replaceToken, logout } = useAuth()
  const navigate = useNavigate()
  const router = useRouter()
  const search = router.state.location.search as { next?: string } | undefined
  const [customerContexts, setCustomerContexts] = React.useState<api.CustomerContext[] | null>(null)
  const [contextError, setContextError] = React.useState<string | null>(null)
  const [selectingCustomerId, setSelectingCustomerId] = React.useState<number | null>(null)
  const normalizedNext = normalizeNextPath(search?.next)
  const attempted = targetForPath(normalizedNext)
  const vaktContexts = React.useMemo(
    () => (customerContexts ?? []).filter((context) => context.can_access_vakt),
    [customerContexts],
  )
  const selectedContext = React.useMemo(
    () => vaktContexts.find((context) => context.selected),
    [vaktContexts],
  )
  const adminAvailable = Boolean(user && canOpenAdmin(user))
  const hasRequestedAccess = attempted === 'admin'
    ? adminAvailable
    : attempted === 'vakt'
      ? vaktContexts.length > 0
      : adminAvailable || vaktContexts.length > 0

  React.useEffect(() => {
    let cancelled = false
    if (!user) {
      setCustomerContexts([])
      return () => {
        cancelled = true
      }
    }
    setCustomerContexts(null)
    setContextError(null)
    api.getCustomerContexts()
      .then((result) => {
        if (!cancelled) setCustomerContexts(result.contexts)
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setContextError(error.message || 'Kunne ikke hente legevakttilganger')
          setCustomerContexts([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [user?.token])

  const openVaktContext = React.useCallback(async (
    context: api.CustomerContext,
    href = '/vakt/',
  ) => {
    setSelectingCustomerId(context.customer_id)
    setContextError(null)
    try {
      const token = await api.selectCustomerContext(context.customer_id)
      replaceToken(token)
      window.location.assign(href)
    } catch (error) {
      setContextError(error instanceof Error ? error.message : 'Kunne ikke velge legevakt')
      setSelectingCustomerId(null)
    }
  }, [replaceToken])

  React.useEffect(() => {
    if (!user) {
      navigate({ to: '/login', search: search?.next ? { next: search.next } : undefined, replace: true })
      return
    }
    if (customerContexts === null || selectingCustomerId !== null) return

    if (attempted === 'admin' && adminAvailable && normalizedNext) {
      window.location.assign(normalizedNext)
      return
    }
    if (attempted === 'vakt' && normalizedNext) {
      if (selectedContext) {
        window.location.assign(normalizedNext)
      } else if (vaktContexts.length === 1) {
        void openVaktContext(vaktContexts[0], normalizedNext)
      }
      return
    }
    if (!attempted) {
      const optionCount = (adminAvailable ? 1 : 0) + vaktContexts.length
      if (optionCount === 1 && adminAvailable) {
        window.location.assign('/admin/')
      } else if (optionCount === 1 && vaktContexts.length === 1) {
        if (selectedContext) window.location.assign('/vakt/')
        else void openVaktContext(vaktContexts[0])
      }
    }
  }, [
    adminAvailable,
    attempted,
    customerContexts,
    navigate,
    normalizedNext,
    openVaktContext,
    search?.next,
    selectedContext,
    selectingCustomerId,
    user,
    vaktContexts,
  ])

  function onLogout() {
    logout()
    navigate({ to: '/login', replace: true })
  }

  if (!user || customerContexts === null || selectingCustomerId !== null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
        <Typography color="text.secondary">Sender videre...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <PortalBrand compact />
            <Tooltip title="Logg ut">
              <IconButton aria-label="Logg ut" onClick={onLogout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {contextError && <Alert severity="error" sx={{ mb: 2 }}>{contextError}</Alert>}

          {hasRequestedAccess ? (
            <Stack spacing={2}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                {vaktContexts.length > 1 ? 'Velg legevakt og flate' : 'Velg flate'}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {adminAvailable && (
                  <SurfaceButton
                    surface={{
                      id: 'admin',
                      title: 'Admin',
                      description: 'Kundeoppsett, rapporter, brukere og drift.',
                      href: '/admin/',
                    }}
                  />
                )}
                {vaktContexts.map((context) => (
                  <SurfaceButton
                    key={context.customer_id}
                    surface={{
                      id: 'vakt',
                      title: context.customer_name,
                      description: `Åpne Min Vakt for ${context.customer_name}.`,
                      href: '/vakt/',
                    }}
                    onOpen={() => void openVaktContext(
                      context,
                      attempted === 'vakt' && normalizedNext ? normalizedNext : '/vakt/',
                    )}
                  />
                ))}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                Ingen tilgang
              </Typography>
              <Alert severity="warning">
                Brukeren har ikke tilgang til denne flaten. Kontakt administrator hvis dette er feil.
              </Alert>
              {attempted && (
                <Typography variant="body2" color="text.secondary">
                  Forespurt flate: {attempted === 'admin' ? 'Admin' : 'Vakt'}.
                </Typography>
              )}
              <Button variant="outlined" startIcon={<LogoutIcon />} onClick={onLogout} sx={{ alignSelf: 'flex-start' }}>
                Logg ut
              </Button>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  )
}
